'use client';

import React, { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Upload, CheckCircle, X, Loader2, FileText, Image } from 'lucide-react';
import SuccessModal from './SuccessModal';

const SA_PROVINCES = [
  'Gauteng',
  'Western Cape',
  'Eastern Cape',
  'KwaZulu-Natal',
  'Limpopo',
  'Mpumalanga',
  'Free State',
  'North West',
  'Northern Cape',
];

interface UploadedFile {
  name: string;
  size: number;
  type: string;
}

interface FormData {
  nameSurname: string;
  age: number;
  dateOfBirth: string;
  homeAddress: string;
  town: string;
  province: string;
  contactEmail: string;
  calls: string;
  whatsapp: string;
  facebookHandle: string;
  instagramHandle: string;
  tiktokHandle: string;
}

function FileUploadField({
  id,
  label,
  accept,
  required,
  hint,
  onFileChange,
  uploadedFile,
  error,
}: {
  id: string;
  label: string;
  accept: string;
  required?: boolean;
  hint?: string;
  onFileChange: (file: UploadedFile | null) => void;
  uploadedFile: UploadedFile | null;
  error?: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onFileChange({ name: file.name, size: file.size, type: file.type });
    }
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isImage = uploadedFile?.type.startsWith('image/');

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-600" style={{ color: '#000000' }}>
        {label}
        {required && <span style={{ color: '#800000' }} className="ml-1">*</span>}
      </label>
      {hint && <p className="text-xs" style={{ color: '#6b6b6b' }}>{hint}</p>}

      <div
        className={`upload-zone rounded-xl p-4 text-center ${uploadedFile ? 'has-file' : ''}`}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.click()}
        aria-label={`Upload ${label}`}
      >
        <input
          ref={inputRef}
          id={id}
          type="file"
          accept={accept}
          className="hidden"
          onChange={handleChange}
        />

        {uploadedFile ? (
          <div className="flex items-center gap-3 justify-between">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {isImage ? (
                <Image size={20} color="#800000" />
              ) : (
                <FileText size={20} color="#800000" />
              )}
              <div className="text-left min-w-0">
                <p className="text-sm font-600 truncate" style={{ color: '#800000' }}>
                  {uploadedFile.name}
                </p>
                <p className="text-xs" style={{ color: '#6b6b6b' }}>
                  {formatSize(uploadedFile.size)}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onFileChange(null);
                if (inputRef.current) inputRef.current.value = '';
              }}
              className="p-1 rounded-full hover:bg-red-50 transition-colors"
              aria-label="Remove file"
            >
              <X size={16} color="#800000" />
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 py-2">
            <Upload size={24} color="#D4AF37" />
            <p className="text-sm font-500" style={{ color: '#800000' }}>
              Click to upload or drag & drop
            </p>
            <p className="text-xs" style={{ color: '#9a9a9a' }}>
              {accept.replace(/\./g, '').toUpperCase().split(',').join(', ')}
            </p>
          </div>
        )}
      </div>

      {error && (
        <p className="text-xs font-500" style={{ color: '#c0392b' }}>
          {error}
        </p>
      )}
    </div>
  );
}

export default function RegistrationForm() {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<FormData>();

  const [showModal, setShowModal] = useState(false);
  const [files, setFiles] = useState<{
    studentCard: UploadedFile | null;
    proofOfPayment: UploadedFile | null;
    faceImage: UploadedFile | null;
    fullLengthImage: UploadedFile | null;
  }>({
    studentCard: null,
    proofOfPayment: null,
    faceImage: null,
    fullLengthImage: null,
  });
  const [fileErrors, setFileErrors] = useState<{
    studentCard?: string;
    proofOfPayment?: string;
    faceImage?: string;
    fullLengthImage?: string;
  }>({});

  const validateFiles = () => {
    const errs: typeof fileErrors = {};
    if (!files.studentCard) errs.studentCard = 'Student card is required';
    if (!files.proofOfPayment) errs.proofOfPayment = 'Proof of payment is required';
    if (!files.faceImage) errs.faceImage = 'Clear face image is required';
    if (!files.fullLengthImage) errs.fullLengthImage = 'Full length image is required';
    setFileErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const onSubmit = async (data: FormData) => {
    if (!validateFiles()) return;

    try {
      const response = await fetch('/api/submit-registration', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!result.success) {
        console.error('Submission failed:', result.error);
        // Still show modal so user knows we received their entry
      }
    } catch (err) {
      console.error('Network error during submission:', err);
    }

    setShowModal(true);
    reset();
    setFiles({ studentCard: null, proofOfPayment: null, faceImage: null, fullLengthImage: null });
    setFileErrors({});
  };

  return (
    <section id="register" className="py-16 sm:py-20 px-4" style={{ background: '#ffffff' }}>
      <div className="max-w-screen-lg mx-auto">
        {/* Heading */}
        <div className="text-center mb-12">
          <h2 className="font-800 text-2xl sm:text-3xl" style={{ color: '#800000' }}>
            REGISTER FOR MISS UNIVERSITIES
            <br className="hidden sm:block" /> OF GAUTENG
          </h2>
          <div className="section-divider mt-4" />
          <p className="text-sm mt-4" style={{ color: '#6b6b6b' }}>
            All fields marked with <span style={{ color: '#800000' }}>*</span> are required
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          {/* Personal Information */}
          <div
            className="rounded-2xl p-6 sm:p-8 mb-6 shadow-card"
            style={{ background: '#faf9f7', border: '1px solid #e2ddd6' }}
          >
            <h3 className="font-700 text-base mb-6 flex items-center gap-2" style={{ color: '#800000' }}>
              <span
                className="w-6 h-6 rounded-full text-xs flex items-center justify-center font-700"
                style={{ background: '#800000', color: '#D4AF37' }}
              >
                1
              </span>
              Personal Information
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {/* Name & Surname */}
              <div className="sm:col-span-2 flex flex-col gap-1.5">
                <label htmlFor="nameSurname" className="text-sm font-600" style={{ color: '#000000' }}>
                  Name &amp; Surname <span style={{ color: '#800000' }}>*</span>
                </label>
                <input
                  id="nameSurname"
                  type="text"
                  placeholder="e.g. Thandi Dlamini"
                  className="input-focus w-full px-4 py-3 rounded-lg text-sm border"
                  style={{ borderColor: errors.nameSurname ? '#c0392b' : '#e2ddd6', color: '#000000', background: '#ffffff' }}
                  {...register('nameSurname', {
                    required: 'Name and surname are required',
                    minLength: { value: 3, message: 'Please enter your full name' },
                  })}
                />
                {errors.nameSurname && (
                  <p className="text-xs font-500" style={{ color: '#c0392b' }}>
                    {errors.nameSurname.message}
                  </p>
                )}
              </div>

              {/* Age */}
              <div className="flex flex-col gap-1.5">
                <label htmlFor="age" className="text-sm font-600" style={{ color: '#000000' }}>
                  Age <span style={{ color: '#800000' }}>*</span>
                </label>
                <input
                  id="age"
                  type="number"
                  placeholder="e.g. 21"
                  min={16}
                  max={45}
                  className="input-focus w-full px-4 py-3 rounded-lg text-sm border"
                  style={{ borderColor: errors.age ? '#c0392b' : '#e2ddd6', color: '#000000', background: '#ffffff' }}
                  {...register('age', {
                    required: 'Age is required',
                    min: { value: 16, message: 'Must be at least 16 years old' },
                    max: { value: 45, message: 'Must be 45 or younger' },
                  })}
                />
                {errors.age && (
                  <p className="text-xs font-500" style={{ color: '#c0392b' }}>
                    {errors.age.message}
                  </p>
                )}
              </div>

              {/* Date of Birth */}
              <div className="flex flex-col gap-1.5">
                <label htmlFor="dateOfBirth" className="text-sm font-600" style={{ color: '#000000' }}>
                  Date of Birth <span style={{ color: '#800000' }}>*</span>
                </label>
                <input
                  id="dateOfBirth"
                  type="date"
                  className="input-focus w-full px-4 py-3 rounded-lg text-sm border"
                  style={{ borderColor: errors.dateOfBirth ? '#c0392b' : '#e2ddd6', color: '#000000', background: '#ffffff' }}
                  {...register('dateOfBirth', {
                    required: 'Date of birth is required',
                  })}
                />
                {errors.dateOfBirth && (
                  <p className="text-xs font-500" style={{ color: '#c0392b' }}>
                    {errors.dateOfBirth.message}
                  </p>
                )}
              </div>

              {/* Home Address */}
              <div className="sm:col-span-2 flex flex-col gap-1.5">
                <label htmlFor="homeAddress" className="text-sm font-600" style={{ color: '#000000' }}>
                  Home Address <span style={{ color: '#800000' }}>*</span>
                </label>
                <textarea
                  id="homeAddress"
                  rows={3}
                  placeholder="Street address, suburb..."
                  className="input-focus w-full px-4 py-3 rounded-lg text-sm border resize-none"
                  style={{ borderColor: errors.homeAddress ? '#c0392b' : '#e2ddd6', color: '#000000', background: '#ffffff' }}
                  {...register('homeAddress', {
                    required: 'Home address is required',
                  })}
                />
                {errors.homeAddress && (
                  <p className="text-xs font-500" style={{ color: '#c0392b' }}>
                    {errors.homeAddress.message}
                  </p>
                )}
              </div>

              {/* Town */}
              <div className="flex flex-col gap-1.5">
                <label htmlFor="town" className="text-sm font-600" style={{ color: '#000000' }}>
                  Town <span style={{ color: '#800000' }}>*</span>
                </label>
                <input
                  id="town"
                  type="text"
                  placeholder="e.g. Johannesburg"
                  className="input-focus w-full px-4 py-3 rounded-lg text-sm border"
                  style={{ borderColor: errors.town ? '#c0392b' : '#e2ddd6', color: '#000000', background: '#ffffff' }}
                  {...register('town', {
                    required: 'Town is required',
                  })}
                />
                {errors.town && (
                  <p className="text-xs font-500" style={{ color: '#c0392b' }}>
                    {errors.town.message}
                  </p>
                )}
              </div>

              {/* Province */}
              <div className="flex flex-col gap-1.5">
                <label htmlFor="province" className="text-sm font-600" style={{ color: '#000000' }}>
                  Province <span style={{ color: '#800000' }}>*</span>
                </label>
                <select
                  id="province"
                  className="input-focus w-full px-4 py-3 rounded-lg text-sm border appearance-none"
                  style={{ borderColor: errors.province ? '#c0392b' : '#e2ddd6', color: '#000000', background: '#ffffff' }}
                  {...register('province', {
                    required: 'Province is required',
                  })}
                >
                  <option value="">Select province...</option>
                  {SA_PROVINCES.map((prov) => (
                    <option key={`prov-${prov}`} value={prov}>
                      {prov}
                    </option>
                  ))}
                </select>
                {errors.province && (
                  <p className="text-xs font-500" style={{ color: '#c0392b' }}>
                    {errors.province.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Contact Details */}
          <div
            className="rounded-2xl p-6 sm:p-8 mb-6 shadow-card"
            style={{ background: '#faf9f7', border: '1px solid #e2ddd6' }}
          >
            <h3 className="font-700 text-base mb-6 flex items-center gap-2" style={{ color: '#800000' }}>
              <span
                className="w-6 h-6 rounded-full text-xs flex items-center justify-center font-700"
                style={{ background: '#800000', color: '#D4AF37' }}
              >
                2
              </span>
              Contact Details
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {/* Email */}
              <div className="sm:col-span-2 flex flex-col gap-1.5">
                <label htmlFor="contactEmail" className="text-sm font-600" style={{ color: '#000000' }}>
                  Contact Email <span style={{ color: '#800000' }}>*</span>
                </label>
                <p className="text-xs" style={{ color: '#6b6b6b' }}>
                  Your pageant book will be sent to this email
                </p>
                <input
                  id="contactEmail"
                  type="email"
                  placeholder="youremail@example.com"
                  className="input-focus w-full px-4 py-3 rounded-lg text-sm border"
                  style={{ borderColor: errors.contactEmail ? '#c0392b' : '#e2ddd6', color: '#000000', background: '#ffffff' }}
                  {...register('contactEmail', {
                    required: 'Email is required',
                    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
                  })}
                />
                {errors.contactEmail && (
                  <p className="text-xs font-500" style={{ color: '#c0392b' }}>
                    {errors.contactEmail.message}
                  </p>
                )}
              </div>

              {/* Calls */}
              <div className="flex flex-col gap-1.5">
                <label htmlFor="calls" className="text-sm font-600" style={{ color: '#000000' }}>
                  Phone (Calls) <span style={{ color: '#800000' }}>*</span>
                </label>
                <input
                  id="calls"
                  type="tel"
                  placeholder="+27 XX XXX XXXX"
                  className="input-focus w-full px-4 py-3 rounded-lg text-sm border"
                  style={{ borderColor: errors.calls ? '#c0392b' : '#e2ddd6', color: '#000000', background: '#ffffff' }}
                  {...register('calls', {
                    required: 'Phone number is required',
                    pattern: { value: /^[\+\d\s\-\(\)]{8,15}$/, message: 'Enter a valid phone number' },
                  })}
                />
                {errors.calls && (
                  <p className="text-xs font-500" style={{ color: '#c0392b' }}>
                    {errors.calls.message}
                  </p>
                )}
              </div>

              {/* WhatsApp */}
              <div className="flex flex-col gap-1.5">
                <label htmlFor="whatsapp" className="text-sm font-600" style={{ color: '#000000' }}>
                  WhatsApp <span style={{ color: '#800000' }}>*</span>
                </label>
                <input
                  id="whatsapp"
                  type="tel"
                  placeholder="+27 XX XXX XXXX"
                  className="input-focus w-full px-4 py-3 rounded-lg text-sm border"
                  style={{ borderColor: errors.whatsapp ? '#c0392b' : '#e2ddd6', color: '#000000', background: '#ffffff' }}
                  {...register('whatsapp', {
                    required: 'WhatsApp number is required',
                    pattern: { value: /^[\+\d\s\-\(\)]{8,15}$/, message: 'Enter a valid WhatsApp number' },
                  })}
                />
                {errors.whatsapp && (
                  <p className="text-xs font-500" style={{ color: '#c0392b' }}>
                    {errors.whatsapp.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Social Media */}
          <div
            className="rounded-2xl p-6 sm:p-8 mb-6 shadow-card"
            style={{ background: '#faf9f7', border: '1px solid #e2ddd6' }}
          >
            <h3 className="font-700 text-base mb-2 flex items-center gap-2" style={{ color: '#800000' }}>
              <span
                className="w-6 h-6 rounded-full text-xs flex items-center justify-center font-700"
                style={{ background: '#800000', color: '#D4AF37' }}
              >
                3
              </span>
              Social Media
              <span className="text-xs font-400 ml-1" style={{ color: '#9a9a9a' }}>(Optional)</span>
            </h3>
            <p className="text-xs mb-6 ml-8" style={{ color: '#6b6b6b' }}>
              Help us tag and promote you on social media
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {/* Facebook */}
              <div className="flex flex-col gap-1.5">
                <label htmlFor="facebookHandle" className="text-sm font-600" style={{ color: '#000000' }}>
                  Facebook Handle
                </label>
                <input
                  id="facebookHandle"
                  type="text"
                  placeholder="@yourname"
                  className="input-focus w-full px-4 py-3 rounded-lg text-sm border"
                  style={{ borderColor: '#e2ddd6', color: '#000000', background: '#ffffff' }}
                  {...register('facebookHandle')}
                />
              </div>

              {/* Instagram */}
              <div className="flex flex-col gap-1.5">
                <label htmlFor="instagramHandle" className="text-sm font-600" style={{ color: '#000000' }}>
                  Instagram Handle
                </label>
                <input
                  id="instagramHandle"
                  type="text"
                  placeholder="@yourname"
                  className="input-focus w-full px-4 py-3 rounded-lg text-sm border"
                  style={{ borderColor: '#e2ddd6', color: '#000000', background: '#ffffff' }}
                  {...register('instagramHandle')}
                />
              </div>

              {/* TikTok */}
              <div className="flex flex-col gap-1.5">
                <label htmlFor="tiktokHandle" className="text-sm font-600" style={{ color: '#000000' }}>
                  TikTok Handle
                </label>
                <input
                  id="tiktokHandle"
                  type="text"
                  placeholder="@yourname"
                  className="input-focus w-full px-4 py-3 rounded-lg text-sm border"
                  style={{ borderColor: '#e2ddd6', color: '#000000', background: '#ffffff' }}
                  {...register('tiktokHandle')}
                />
              </div>
            </div>
          </div>

          {/* Document Uploads */}
          <div
            className="rounded-2xl p-6 sm:p-8 mb-8 shadow-card"
            style={{ background: '#faf9f7', border: '1px solid #e2ddd6' }}
          >
            <h3 className="font-700 text-base mb-2 flex items-center gap-2" style={{ color: '#800000' }}>
              <span
                className="w-6 h-6 rounded-full text-xs flex items-center justify-center font-700"
                style={{ background: '#800000', color: '#D4AF37' }}
              >
                4
              </span>
              Required Documents &amp; Images
            </h3>
            <p className="text-xs mb-6 ml-8" style={{ color: '#6b6b6b' }}>
              All uploads in this section are compulsory
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <FileUploadField
                id="studentCard"
                label="Upload Student Card"
                accept=".jpg,.jpeg,.png,.pdf"
                required
                hint="JPG, PNG or PDF accepted"
                uploadedFile={files.studentCard}
                onFileChange={(f) => {
                  setFiles((prev) => ({ ...prev, studentCard: f }));
                  if (f) setFileErrors((prev) => ({ ...prev, studentCard: undefined }));
                }}
                error={fileErrors.studentCard}
              />

              <FileUploadField
                id="proofOfPayment"
                label="Upload Proof of Payment"
                accept=".jpg,.jpeg,.png,.pdf"
                required
                hint="Screenshot or PDF of bank transfer"
                uploadedFile={files.proofOfPayment}
                onFileChange={(f) => {
                  setFiles((prev) => ({ ...prev, proofOfPayment: f }));
                  if (f) setFileErrors((prev) => ({ ...prev, proofOfPayment: undefined }));
                }}
                error={fileErrors.proofOfPayment}
              />

              <FileUploadField
                id="faceImage"
                label="Upload Clear Face Image"
                accept=".jpg,.jpeg,.png"
                required
                hint="Clear, well-lit headshot — JPG or PNG only"
                uploadedFile={files.faceImage}
                onFileChange={(f) => {
                  setFiles((prev) => ({ ...prev, faceImage: f }));
                  if (f) setFileErrors((prev) => ({ ...prev, faceImage: undefined }));
                }}
                error={fileErrors.faceImage}
              />

              <FileUploadField
                id="fullLengthImage"
                label="Upload Full Length Image"
                accept=".jpg,.jpeg,.png"
                required
                hint="Full body photo, standing — JPG or PNG only"
                uploadedFile={files.fullLengthImage}
                onFileChange={(f) => {
                  setFiles((prev) => ({ ...prev, fullLengthImage: f }));
                  if (f) setFileErrors((prev) => ({ ...prev, fullLengthImage: undefined }));
                }}
                error={fileErrors.fullLengthImage}
              />
            </div>
          </div>

          {/* Submit */}
          <div className="text-center">
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center gap-3 px-10 py-4 rounded-full font-700 text-base tracking-widest btn-gold shadow-gold disabled:opacity-70 disabled:cursor-not-allowed"
              style={{ minWidth: 220 }}
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  <span>SUBMITTING...</span>
                </>
              ) : (
                <>
                  <CheckCircle size={20} />
                  <span>SUBMIT ENTRY</span>
                </>
              )}
            </button>
            <p className="text-xs mt-3" style={{ color: '#9a9a9a' }}>
              By submitting you agree to our terms and conditions
            </p>
          </div>
        </form>
      </div>

      {showModal && <SuccessModal onClose={() => setShowModal(false)} />}
    </section>
  );
}